// Real authentication context backed by /api/auth/* (httpOnly cookie JWT +
// rotating refresh token). The exported surface is unchanged from the old
// localStorage implementation so LoginPage/RegisterPage/ProtectedRoute keep
// working, but admin helpers (approveUser etc.) are now async API calls.

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, setOnUnauthorized } from '../api/client';
import { clearApiCache } from '../hooks/useApi';
import ENDPOINTS from '../api/endpoints';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [entitlements, setEntitlements] = useState({});
  const [settings, setSettings] = useState({});
  const [impersonating, setImpersonating] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const clearAuthState = useCallback(() => {
    setUser(null);
    setEntitlements({});
    setSettings({});
    setImpersonating(null);
    clearApiCache();
  }, []);

  // Bootstrap: ask the server who we are (cookie travels automatically).
  useEffect(() => {
    let cancelled = false;
    setOnUnauthorized(() => clearAuthState());

    (async () => {
      try {
        const data = await apiFetch(ENDPOINTS.auth.me);
        if (!cancelled) {
          setUser(data.user);
          setEntitlements(data.entitlements || {});
          setSettings(data.settings || {});
          setImpersonating(data.impersonating || null);
        }
      } catch {
        if (!cancelled) clearAuthState();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      setOnUnauthorized(null);
    };
  }, [clearAuthState]);

  const login = async (email, password) => {
    try {
      const data = await apiFetch(ENDPOINTS.auth.login, { method: 'POST', body: { email, password } });
      setUser(data.user);
      setEntitlements(data.entitlements || {});
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Finishes an invite or reset link. The server signs this browser in;
  // validateSession() then loads user, entitlements and settings exactly as
  // a fresh page load would (impersonate()/stopImpersonating() do the same).
  const setPasswordWithToken = async (token, password) => {
    try {
      const data = await apiFetch(ENDPOINTS.auth.setPassword, { method: 'POST', body: { token, password } });
      clearApiCache();
      await validateSession();
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message, status: error.status };
    }
  };

  const register = async ({ name, email, company, message, password }) => {
    try {
      await apiFetch(ENDPOINTS.auth.register, {
        method: 'POST',
        body: { name, email, company, message, password },
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await apiFetch(ENDPOINTS.auth.logout, { method: 'POST' });
    } catch {
      // Cookies are cleared server-side on best effort; state clears regardless.
    }
    clearAuthState();
    navigate('/');
  };

  // Admin helpers — now async, backed by /api/admin/users.
  const approveUser = async (userId) => {
    await apiFetch(ENDPOINTS.admin.users, { method: 'POST', body: { action: 'approve', userId } });
  };

  const rejectUser = async (userId) => {
    await apiFetch(ENDPOINTS.admin.users, { method: 'POST', body: { action: 'reject', userId } });
  };

  const getPendingUsers = async () => {
    const data = await apiFetch(ENDPOINTS.admin.usersByStatus('pending'));
    return data.users || [];
  };

  const getApprovedUsers = async () => {
    const data = await apiFetch(ENDPOINTS.admin.usersByStatus('active'));
    return data.users || [];
  };

  const listClients = async (q) => {
    const data = await apiFetch(ENDPOINTS.admin.clients(q));
    return data.users || [];
  };

  // Invite flow: the server creates the account and returns a one-time link.
  const inviteClient = (fields) =>
    apiFetch(ENDPOINTS.admin.users, { method: 'POST', body: { action: 'createClient', ...fields } });

  const resendInvite = (userId) =>
    apiFetch(ENDPOINTS.admin.users, { method: 'POST', body: { action: 'resendInvite', userId } });

  const sendResetLink = (userId) =>
    apiFetch(ENDPOINTS.admin.users, { method: 'POST', body: { action: 'sendReset', userId } });

  // The server ends every other session and sets fresh cookies for this one.
  const changePassword = (currentPassword, newPassword) =>
    apiFetch(ENDPOINTS.auth.changePassword, { method: 'POST', body: { currentPassword, newPassword } });

  // Identity swaps must flush the API cache, or the new identity reads the
  // previous one's cached responses.
  const impersonate = async (userId) => {
    await apiFetch(ENDPOINTS.auth.impersonate, { method: 'POST', body: { action: 'start', userId } });
    clearApiCache();
    await validateSession();
  };

  const stopImpersonating = async () => {
    await apiFetch(ENDPOINTS.auth.impersonate, { method: 'POST', body: { action: 'stop' } });
    clearApiCache();
    await validateSession();
  };

  const updateUser = (updates) => {
    setUser((current) => (current ? { ...current, ...updates } : current));
  };

  // Re-checks the session against the server; also refreshes entitlements
  // (used after checkout success so unlocked panels appear without re-login).
  const validateSession = async () => {
    try {
      const data = await apiFetch(ENDPOINTS.auth.me);
      setUser(data.user);
      setEntitlements(data.entitlements || {});
      setSettings(data.settings || {});
      setImpersonating(data.impersonating || null);
      return true;
    } catch {
      clearAuthState();
      return false;
    }
  };

  const value = {
    user,
    entitlements,
    settings,
    isAuthenticated: Boolean(user),
    isLoading,
    login,
    logout,
    register,
    setPasswordWithToken,
    approveUser,
    rejectUser,
    getPendingUsers,
    getApprovedUsers,
    listClients,
    inviteClient,
    resendInvite,
    sendResetLink,
    changePassword,
    impersonating,
    impersonate,
    stopImpersonating,
    updateUser,
    validateSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
